import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  ViewEncapsulation,
} from '@angular/core';
import { ButtonModule } from '@app/components/atoms/button/button.module';
import { ActionItemComponent } from '@app/components/organisms/action-item/action-item.component';
import {
  DEFAULT_CONFIRM_BTN_CLASS,
  DEFAULT_DISCARD_BTN_CLASS,
  DEFAULT_MESSAGE_WIDTH,
  MISSION_CONSIDERATIONS,
  MISSION_CONSIDERATIONS_TITLE,
  MISSION_INFO_MESSAGE,
  MISSION_INFO_MESSAGEALT,
} from '@app/constants';

type Behavior = { title: string; description: string };
@Component({
  selector: 'app-form-confirm-discard',
  standalone: true,
  imports: [CommonModule, ButtonModule, ActionItemComponent],
  templateUrl: './form-confirm-discard.component.html',
  styleUrls: ['./form-confirm-discard.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'form-confirm-discard--flush' },
})
export class FormConfirmDiscardComponent implements OnChanges {
  /** Texto / UI */
  @Input() description = '';
  @Input() secondaryDescription = '';
  @Input() thirdDescription = '';
  @Input() iconSrc = '';
  @Input() iconAlt = '';
  @Input() showIcon = true;

  /** Botones */
  @Input() discardLabel = '';
  @Input() confirmLabel = '';
  @Input() discardBtnClass = DEFAULT_DISCARD_BTN_CLASS;
  @Input() confirmBtnClass = DEFAULT_CONFIRM_BTN_CLASS;
  @Input() messageWidth: string = DEFAULT_MESSAGE_WIDTH;
  @Input() showSecondaryButton: boolean = true;
  @Input() showPrimaryButton: boolean = true;
  @Input() isLoading: boolean = false;
  @Input() teamId?: number;
  @Input() header?: string;

  /** Callbacks */
  @Input() onConfirm?: () => void; // Descartar
  @Input() onKeep?: () => void; // Guardar
  @Input() onClose?: () => void;
  @Input() showMissionsSection: boolean = false;
  @Input() showBehaviorsSection = false;
  @Input() showEditorUnsavedSection = false;
  @Input() behaviors: Behavior[] = [];
  @Input() onSubmitSelected?: (items: Behavior[]) => void;
  @Input() preselectedBehaviors: Behavior[] = [];

  missionMessage = MISSION_INFO_MESSAGE;
  missionMessageAlt = MISSION_INFO_MESSAGEALT;
  missionConsiderationsTitle = MISSION_CONSIDERATIONS_TITLE;
  missionConsiderations = MISSION_CONSIDERATIONS;
  selectedBehaviors: Behavior[] = [];
  availableBehaviors: Behavior[] = [];

  get isSubmitDisabled(): boolean {
    return this.selectedBehaviors.length === 0;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['behaviors'] || changes['preselectedBehaviors']) {
      const pre = this.preselectedBehaviors ?? [];
      const all = this.behaviors ?? [];

      this.selectedBehaviors = [...pre];

      this.availableBehaviors = all.filter((b) => !pre.some((p) => this.sameBehavior(p, b)));
    }
  }

  addBehavior(index: number) {
    const item = this.availableBehaviors[index];
    if (!item) return;
    this.availableBehaviors = this.availableBehaviors.filter((_, i) => i !== index);

    if (!this.selectedBehaviors.some((s) => this.sameBehavior(s, item))) {
      this.selectedBehaviors = [item, ...this.selectedBehaviors];
    }
  }

  removeBehavior(index: number) {
    const item = this.selectedBehaviors[index];
    if (!item) return;
    this.selectedBehaviors = this.selectedBehaviors.filter((_, i) => i !== index);

    if (!this.availableBehaviors.some((a) => this.sameBehavior(a, item))) {
      this.availableBehaviors = [item, ...this.availableBehaviors];
    }
  }

  submitSelection() {
    this.onSubmitSelected?.([...this.selectedBehaviors]);
  }

  private sameBehavior = (a: Behavior, b: Behavior) =>
    a.title.trim() === b.title.trim() && a.description.trim() === b.description.trim();
}
